<template>
    <div>
        <NuxtLoadingIndicator color="repeating-linear-gradient(to right, #DFE7EC 0%, #153F62 100%)" />
        <div class="max-w-7xl ml-auto mr-auto xl:max-w-small-desktop-container 2xl:max-w-desktop-container">
            <div class="website-header__wrapper">
                <div class="flex items-center w-full">
                    <div
                        class="flex-1"
                        v-html="(websiteUSPHeaderHtml ? websiteUSPHeaderHtml : '') + headerHtml + navigationHtml"
                    ></div>
                </div>
                <div v-html="navigationHtml"></div>
            </div>

            <slot />
        </div>
    </div>
    <div class="mt-14" v-html="footerHtml"></div>
    <div
        v-if="isCookiesDisabled"
        class="fixed bottom-4 text-white bg-warning-800 width-full p-4 md:w-1/2 left-[50%] -translate-x-1/2 z-[3000]"
    >
        {{ $t('Bbk.global.disabledCookies') }}
    </div>
</template>

<script setup lang="ts">
    import queryPageElements from '@/graphql/queries/queryPageElements.graphql'
    import { useGlobalStore } from '~/store/global'
    import { useLanguageSwitcher } from '~/composables/useLanguageSwitcher'

    const isCookiesDisabled = ref(false)
    const { URL_CHECKOUT_CART } = useAppConfig()

    const { $i18n } = useNuxtApp()
    const lang = $i18n.locale.value
    const { siteUrl } = useRuntimeConfig().public
    const route = useRoute()

    const pageElementsData =
        process.client && window?.__NUXT__?.data.pageElementsData
            ? { value: window.__NUXT__.data.pageElementsData }
            : (
                  await useAsyncQuery({
                      query: queryPageElements,
                      key: 'pageElementsData',
                  })
              ).data

    const headerHtml = ref<string>(pageElementsData?.value?.pageElements?.websiteHeaderContainer)

    const headerStyles = ref<string>(pageElementsData?.value?.pageElements?.headerStyles)

    const navigationHtml = ref<string>(pageElementsData?.value?.pageElements?.websiteNavigationContainer)

    const footerHtml = ref<string>(pageElementsData?.value?.pageElements?.footerContainer)
    const footerStyles = ref<string>(pageElementsData?.value?.pageElements?.footerStyles)

    const websiteUSPHeaderHtml = ref<string>(pageElementsData?.value?.pageElements?.websiteUSPHeader)

    const isCartPage = route.path === `/${$i18n.locale.value}${URL_CHECKOUT_CART}`

    if (isCartPage) {
        websiteUSPHeaderHtml.value = ''
    }

    const globalStore = useGlobalStore()

    globalStore.setStoreName(lang)

    const { salesManagoAppSmId } = useRuntimeConfig().public

    useHead({
        htmlAttrs: {
            lang,
        },
        link: computed(() => {
            const getHref = (code: string) => `${siteUrl}/${code}${route.path.substring(3)}`

            const linksAlternate = $i18n.locales.value.map((locale) => {
                const code = (typeof locale !== 'string' && locale.code).toString()

                return {
                    rel: 'alternate',
                    hreflang: code.toString(),
                    href: getHref(code),
                }
            })

            linksAlternate.push({
                rel: 'alternate',
                hreflang: 'x-default',
                href: getHref($i18n.fallbackLocale.value as string),
            })

            return linksAlternate
        }),
        style: [headerStyles.value, footerStyles.value],
        script: [
            {
                innerHTML: `
                var _smid = "${salesManagoAppSmId}";
                var _smapp = 1;
                var _smcustom = true;
                var _smbanners = true;
                (function(w, r, a, sm, s ) {
                    w['SalesmanagoObject'] = r;
                    w[r] = w[r] || function () {( w[r].q = w[r].q || [] ).push(arguments)};
                    sm = document.createElement('script'); sm.type = 'text/javascript'; sm.async = true; sm.src = a;
                    s = document.getElementsByTagName('script')[0];
                    s.parentNode.insertBefore(sm, s);
                })(window, 'sm', ('https:' == document.location.protocol ? 'https://' : 'http://') + 'www.salesmanago.pl/static/sm.js');
              `,
                type: 'text/javascript',
                body: true,
            },
            {
                src: `https://www.salesmanago.pl/dynamic/${salesManagoAppSmId}/popups.js`,
                async: true,
                body: true,
            },
            {
                src: 'https://consent.cookiebot.com/uc.js',
                id: 'Cookiebot',
                type: 'text/javascript',
                'data-cbid': 'd4dd17c3-2e22-4ec6-b553-d00096495b5c',
                'data-blockingmode': 'auto',
                body: true,
            },
        ],
        __dangerouslyDisableSanitizersByTagID: {
            'nuxt-custom-sm-script': ['innerHTML'],
        },
    })

    useLanguageSwitcher('#footer-switcher-list')
    useLanguageSwitcher('.header-switcher__list')

    function init() {
        window.initCategoriesEvents()
        window.initHamburgerEvents()
        window.initSearchEvents()
        window.initFooterAccordionEvents()

        if (window.initNavMenuEvents) {
            window.initNavMenuEvents()
        }

        if (window.initWishlistEvents) {
            window.initWishlistEvents()
        }

        if (window.initUSP && !isCartPage) {
            window.initUSP()
        }

        if (window.initMiniCartEvents) {
            window.initMiniCartEvents()
        }

        if (window.initNewsletterEvents) {
            window.initNewsletterEvents()
        }
    }

    function clear() {
        window.clearCategoriesEvents()
        window.clearHamburgerEvents()
        window.clearSearchEvents()
        window.clearFooterAccordionEvents()

        if (window.clearWishlistEvents) {
            window.clearWishlistEvents()
        }

        if (window.clearMiniCartEvents) {
            window.clearMiniCartEvents()
        }

        if (window.clearNewsletterEvents) {
            window.clearNewsletterEvents()
        }
    }

    onBeforeUnmount(() => {
        clear()
    })

    onMounted(() => {
        if (!navigator.cookieEnabled) {
            isCookiesDisabled.value = true
        }

        initDataLayer()

        clear()
        init()
    })

    const pageTypeEventMap = [
        { key: '/c/', value: 'category' },
        { key: '/p/', value: 'product' },
        { key: '/s/', value: 'search' },
        { key: '/checkout/cart', value: 'cart' },
    ]

    function getPageType(url) {
        for (const entry of pageTypeEventMap) {
            const regex = new RegExp(entry.key)

            if (regex.test(url)) {
                return entry.value
            }
        }

        return 'other'
    }

    function initDataLayer() {
        window.dataLayer = window.dataLayer || []

        if (getPageType(route.fullPath) !== 'search') {
            window.dataLayer.push({
                pageType: getPageType(route.fullPath),
            })
        }
    }
</script>
